/**
 * AssuranceView (P154) -- "answer the regulator".
 *
 * Pick a templated question; the answer is assembled server-side from
 * the computable model (no LLM in the path), so every claim carries
 * citations and chain-backed claims show a "verifiable" badge linking
 * back to the proof. Exec-shaped: admin OR approver.
 */
import { useState } from "react";
import { formatDateTime } from "shared/time/formatDate";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useToast } from "shared/toast/ToastProvider";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { RfiInbox } from "shared/assurance/RfiInbox";
import { useMe } from "shared/api/useMe";
import type { AssuranceCitation, AssuranceClaim } from "shared/api/types";

import { useAssuranceAnswer, useAssuranceQuestions } from "./useAssurance";
import { useDownloadAnswerPack } from "./useDownloadAnswerPack";
import { useDownloadRfiBundle } from "./useDownloadRfiBundle";

function CitationChip({ cite }: { cite: AssuranceCitation }) {
  const { t } = useTranslation();
  const body = (
    <>
      {cite.label}
      {cite.chain && (
        <span
          data-testid="assurance-citation-chain"
          className="ms-1 rounded bg-brand-brass/15 px-1 text-[9px] font-semibold uppercase tracking-wide text-brand-brassDark"
        >
          {t("assurance.verifiable")}
        </span>
      )}
    </>
  );
  const cls =
    "inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-600";
  return cite.surface_url ? (
    <Link
      data-testid="assurance-citation"
      data-chain={cite.chain ?? ""}
      to={cite.surface_url}
      className={`${cls} hover:border-brand-brass/40 hover:text-brand-brassDark`}
    >
      {body}
    </Link>
  ) : (
    <span
      data-testid="assurance-citation"
      data-chain={cite.chain ?? ""}
      className={cls}
    >
      {body}
    </span>
  );
}

function ClaimRow({ claim }: { claim: AssuranceClaim }) {
  return (
    <div
      data-testid="assurance-claim"
      className="border-t border-slate-100 py-2 first:border-t-0"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm text-slate-600">{claim.label}</span>
        <span className="text-sm font-semibold text-slate-900">{claim.value}</span>
      </div>
      {claim.citations.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {claim.citations.map((c, i) => (
            <CitationChip key={`${c.kind}-${c.ref_id}-${i}`} cite={c} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AssuranceView(): JSX.Element {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const questions = useAssuranceQuestions();
  const [selected, setSelected] = useState<string | null>(null);
  const [bundle, setBundle] = useState<Set<string>>(new Set());
  const answer = useAssuranceAnswer(selected);
  const downloadPack = useDownloadAnswerPack();
  const downloadBundle = useDownloadRfiBundle();
  const toast = useToast();

  function toggleBundle(id: string) {
    setBundle((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const canSee = me.data?.is_admin || me.data?.positions.includes("approver");
  if (me.data && !canSee) {
    return (
      <div
        data-testid="assurance-forbidden"
        className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
      >
        {t("assurance.forbidden")}
      </div>
    );
  }

  return (
    <section data-testid="assurance-view" className="flex flex-col gap-4">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          {t("assurance.kicker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="verifier"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("assurance.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">{t("assurance.intro")}</p>
      </header>

      {/* E6 -- a supervisor's question is answered here, where the
          answers already live. */}
      <RfiInbox questionIds={questions.data ?? []} />

      <div className="flex flex-wrap gap-2">
        {(questions.data ?? []).map((q) => (
          <div
            key={q.id}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
              selected === q.id
                ? "border-brand-brass/50 bg-brand-brass/5"
                : "border-slate-200 bg-white hover:border-brand-brass/30"
            }`}
          >
            <input
              type="checkbox"
              data-testid={`assurance-bundle-check-${q.id}`}
              checked={bundle.has(q.id)}
              onChange={() => toggleBundle(q.id)}
              title={t("assurance.rfi.include")}
              className="accent-brand-brass"
            />
            <button
              type="button"
              data-testid={`assurance-question-${q.id}`}
              onClick={() => setSelected(q.id)}
              className={`text-start ${
                selected === q.id ? "text-brand-brassDark" : "text-slate-700"
              }`}
            >
              {t(q.text_key ?? `assurance.questions.${q.id}`, { defaultValue: q.text })}
            </button>
          </div>
        ))}
      </div>

      {/* P177: bundle several answers into one signed RFI pack. */}
      {bundle.size > 0 && (
        <div
          data-testid="assurance-rfi-bar"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-brand-brass/40 bg-brand-brass/5 px-3 py-2"
        >
          <span className="text-sm text-slate-700">
            {t("assurance.rfi.selected", { count: bundle.size })}
          </span>
          <button
            type="button"
            data-testid="assurance-rfi-download"
            onClick={() =>
              downloadBundle.mutate([...bundle], {
                // Round 9 E6 -- a zip appears in a folder the reader is
                // not looking at. The board PDF next door has said
                // "Board snapshot downloaded." since P17; this is the
                // signed artefact that carries the proof, and it was
                // the quieter of the two.
                onSuccess: () =>
                  toast.push({
                    tone: "success",
                    message: t("assurance.rfi.downloaded", { count: bundle.size }),
                  }),
              })
            }
            disabled={downloadBundle.isPending}
            className="rounded border border-brand-brass/40 bg-brand-brass/10 px-3 py-1.5 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/20 disabled:opacity-50"
          >
            {downloadBundle.isPending
              ? t("assurance.downloading")
              : t("assurance.rfi.download")}
          </button>
          <button
            type="button"
            data-testid="assurance-rfi-clear"
            onClick={() => setBundle(new Set())}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            {t("assurance.rfi.clear")}
          </button>
          {downloadBundle.isError && (
            <span className="text-[11px] text-rag-red">
              {t("assurance.downloadError")}
            </span>
          )}
        </div>
      )}

      {!selected && (
        <p data-testid="assurance-pick" className="text-sm text-slate-500">
          {t("assurance.pickPrompt")}
        </p>
      )}

      {selected && answer.isLoading && (
        <p data-testid="assurance-loading" className="text-sm text-slate-500">
          {t("assurance.loading")}
        </p>
      )}
      {selected && answer.isError && (
        <p data-testid="assurance-error" className="text-sm text-rag-red">
          {t("assurance.error")}
        </p>
      )}

      {answer.data && (
        <article
          data-testid="assurance-answer"
          className="rounded-lg border border-slate-200 bg-white p-4"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-900">
              {t(`assurance.questions.${answer.data.question_id}`, {
                defaultValue: answer.data.question_text,
              })}
            </h2>
            <button
              type="button"
              data-testid="assurance-download-pack"
              onClick={() =>
                answer.data &&
                downloadPack.mutate(answer.data.question_id, {
                  onSuccess: () =>
                    toast.push({
                      tone: "success",
                      message: t("assurance.packDownloaded"),
                    }),
                })
              }
              disabled={downloadPack.isPending}
              className="shrink-0 rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1.5 text-xs font-medium text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
            >
              {downloadPack.isPending
                ? t("assurance.downloading")
                : t("assurance.downloadPack")}
            </button>
          </div>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {/* F -- this printed the ISO timestamp the API returned,
                including the microseconds and the Z: a machine value,
                on the surface written for a supervisor. */}
            {t("assurance.asOf")} {formatDateTime(answer.data.as_of, i18n.language)}
          </p>
          {downloadPack.isError && (
            <p
              data-testid="assurance-download-error"
              className="mt-1 text-[11px] text-rag-red"
            >
              {t("assurance.downloadError")}
            </p>
          )}
          {/* F -- the first claim is a headline figure ("4 of 6") and
              ten unlabelled rows followed it, so the count read as a
              count of the rows beneath rather than of the estate. The
              rows are what the answer RESTS ON; saying so separates the
              claim from its workings. */}
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">
            {t("assurance.claimsHeading", { count: answer.data.claims.length })}
          </p>
          <div className="mt-1">
            {answer.data.claims.map((c, i) => (
              <ClaimRow key={`${c.label}-${i}`} claim={c} />
            ))}
          </div>
          {answer.data.note && (
            <p className="mt-3 border-t border-slate-100 pt-2 text-[11px] italic text-slate-400">
              {answer.data.note}
            </p>
          )}
        </article>
      )}
    </section>
  );
}
